import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, AlertCircle, Loader2, CheckCircle, Eye, EyeOff, Info } from 'lucide-react';
import { toast } from 'sonner';

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(null);

  useEffect(() => {
    // Get token from URL
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
    } else {
      setError('Invalid or missing reset token. Please request a new password reset.');
    }

    // Fetch password requirements
    const fetchRequirements = async () => {
      try {
        const response = await axios.get(`${API}/auth/password-requirements`);
        setPasswordRequirements(response.data);
      } catch (err) {
        console.error('Failed to fetch password requirements');
      }
    };
    fetchRequirements();
  }, [searchParams]);

  const validatePassword = (pwd) => {
    const errors = [];
    if (passwordRequirements) {
      if (pwd.length < passwordRequirements.min_length) {
        errors.push(`At least ${passwordRequirements.min_length} characters`);
      }
      if (passwordRequirements.require_uppercase && !/[A-Z]/.test(pwd)) {
        errors.push('One uppercase letter');
      }
      if (passwordRequirements.require_lowercase && !/[a-z]/.test(pwd)) {
        errors.push('One lowercase letter');
      }
      if (passwordRequirements.require_digit && !/\d/.test(pwd)) {
        errors.push('One number');
      }
      if (passwordRequirements.require_special && !/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
        errors.push('One special character');
      }
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setError(`Password requirements not met: ${passwordErrors.join(', ')}`);
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="noise-overlay" />
        
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img 
              src={NBR_LOGO} 
              alt="National Business Register" 
              className="h-12 w-auto"
            />
            <div>
              <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900">MARK</h1>
              <p className="text-xs text-teal-600 tracking-widest uppercase">Trade Mark Monitoring</p>
            </div>
          </div>

          {/* Success Card */}
          <div className="swiss-card p-8 text-center" data-testid="reset-password-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Password Reset Complete</h2>
            <p className="text-slate-500 text-sm mb-6">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no token
  if (!token && error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="noise-overlay" />
        
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img 
              src={NBR_LOGO} 
              alt="National Business Register" 
              className="h-12 w-auto"
            />
            <div>
              <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900">MARK</h1>
              <p className="text-xs text-teal-600 tracking-widest uppercase">Trade Mark Monitoring</p>
            </div>
          </div>

          {/* Error Card */}
          <div className="swiss-card p-8 text-center" data-testid="reset-password-invalid">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Invalid Reset Link</h2>
            <p className="text-slate-500 text-sm mb-6">
              {error}
            </p>
            <Link to="/forgot-password">
              <Button className="bg-slate-900 hover:bg-slate-800">
                Request New Reset Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="noise-overlay" />
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img 
            src={NBR_LOGO} 
            alt="National Business Register" 
            className="h-12 w-auto"
          />
          <div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900">MARK</h1>
            <p className="text-xs text-teal-600 tracking-widest uppercase">Trade Mark Monitoring</p>
          </div>
        </div>

        {/* Reset Password Card */}
        <div className="swiss-card p-8" data-testid="reset-password-card">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Reset Password</h2>
          <p className="text-slate-500 text-sm mb-6">
            Enter your new password below.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2" data-testid="reset-password-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  data-testid="reset-new-password-input"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordRequirements && (
                <div className="text-xs text-slate-500 flex items-start gap-1.5 mt-1">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>{passwordRequirements.requirements}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                data-testid="reset-confirm-password-input"
                className="h-11"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800"
              disabled={loading}
              data-testid="reset-password-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-slate-500 hover:text-slate-900"
              data-testid="back-to-login-link"
            >
              Back to Login
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          UK Trademark Monitoring System
        </p>
      </div>
    </div>
  );
};
